import {rpc, wallet} from '@cityofzion/neon-core'
import {EnvHelper} from '@/helpers/EnvHelper'
import axios from 'axios'
import {NeonInvoker} from '@cityofzion/neon-invoker'
import {BalanceResponse} from '@/helpers/ShrikeHelper'
import {Token} from '@/libs/blockchain-services/types'

export class NeoHelper {
  static neonInvoker: NeonInvoker | null = null
  static readonly rpcUrl = EnvHelper.VUE_APP_RPC_URL
  static readonly attemptsToValidateTransaction =
    EnvHelper.VUE_APP_MAX_ATTEMPTS_TO_VALIDATE_TRANSACTION ?? 25

  static async getNeonInvoker(): Promise<NeonInvoker> {
    if (!this.neonInvoker) {
      this.neonInvoker = await NeonInvoker.init({
        rpcAddress: this.rpcUrl,
        account: new wallet.Account(),
      })
    }

    return this.neonInvoker
  }

  static async validateTransaction(transactionHash: string): Promise<boolean> {
    const rpcClient = new rpc.RPCClient(this.rpcUrl)
    const maxTries = this.attemptsToValidateTransaction

    let tries = 0

    do {
      tries += 1

      await new Promise(resolve => setTimeout(resolve, 4000))

      try {
        const result = await rpcClient.getApplicationLog(transactionHash)

        const execution = result.executions[0]

        if (!execution || !execution.stack || execution.vmstate !== 'HALT')
          break

        const [stack] = execution.stack

        if (!stack || !stack.value) break

        if (execution.notifications.length <= 0) break

        return true
        // eslint-disable-next-line no-empty
      } catch {}
    } while (tries < maxTries)

    return false
  }

  static async getVoterData(address: string) {
    const {data} = await axios.post(this.rpcUrl, {
      jsonrpc: '2.0',
      id: 1,
      method: 'invokefunction',
      params: [
        '0xef4073a0f2b305a38ec4050e4d3d28bc40ea63f5',
        'getAccountState',
        [
          {
            type: 'Hash160',
            value: new wallet.Account(address).scriptHash,
          },
        ],
      ],
    })

    const candidateNeverVoted = data.result.stack[0].type === 'Any'
    if (candidateNeverVoted) {
      return ''
    }

    const voterData = data.result.stack[0].value[2]
    if (voterData.type === 'Any') {
      return ''
    }

    const candidatePubKey = voterData.value

    const candidatePubKeyHex = Buffer.from(candidatePubKey, 'base64').toString(
      'hex'
    )

    return new wallet.Account(candidatePubKeyHex).publicKey
  }

  static async getUnclaimedGasData(address: string) {
    const {data} = await axios.post(this.rpcUrl, {
      jsonrpc: '2.0',
      method: 'getunclaimedgas',
      params: [address],
      id: 1,
    })

    return data.result
  }

  static async calculateUnclaimedGasFee(address: string) {
    const neonInvoker = await this.getNeonInvoker()

    const scriptHash = NeoHelper.getScriptHashFromAddress(address)

    return neonInvoker.calculateFee({
      invocations: [
        {
          operation: 'transfer',
          scriptHash: EnvHelper.VUE_APP_NEO_SCRIPT_HASH,
          args: [
            {
              type: 'Hash160',
              value: scriptHash,
            },
            {
              type: 'Hash160',
              value: scriptHash,
            },
            {
              type: 'Integer',
              value: 0,
            },
            {
              type: 'Any',
              value: null,
            },
          ],
        },
      ],
    })
  }

  static getScriptHashFromAddress(address: string) {
    return `0x${new wallet.Account(address).scriptHash}`
  }

  static isValidNeo3Address(address: string): boolean {
    const neo3Regex = /^[N][1-9A-HJ-NP-Za-km-z]{33}$/ // Base58Check, 34 characters
    return neo3Regex.test(address)
  }

  static isValidNeoXAddress(address: string): boolean {
    const neoXRegex = /^0x[a-fA-F0-9]{40}$/ // Hexadecimal Ethereum-style
    return neoXRegex.test(address)
  }

  static async getNeoXBalance(address: string): Promise<BalanceResponse[]> {
    const {data: nativeBalance} = await axios.get<{coin_balance: string}>(
      `${EnvHelper.DORA_NEOX_URL}/addresses/${address}`
    )
    if (!nativeBalance || 'message' in nativeBalance) {
      throw new Error('Native balance not found')
    }

    const neoXNativeToken = {
      decimals: 18,
      hash: '0xdE41591ED1f8ED1484aC2CD8ca0876428de60EfF',
      symbol: 'WGAS10',
    }

    const neoXDefaultDecimals = 18

    const balances: BalanceResponse[] = [
      {
        amount: String(
          Number(nativeBalance.coin_balance) / 10 ** neoXNativeToken.decimals
        ),
        token: neoXNativeToken,
      },
    ]

    const {data: erc20Balances} = await axios.get(
      `${EnvHelper.DORA_NEOX_URL}/addresses/${address}/token-balances`
    )
    if (!erc20Balances || 'message' in erc20Balances) {
      throw new Error('ERC20 balance not found')
    }

    erc20Balances.forEach((balance: any) => {
      try {
        if (balance.token.type !== 'ERC-20') {
          return
        }

        const token: Token = {
          decimals: balance.token.decimals
            ? parseInt(balance.token.decimals)
            : neoXDefaultDecimals,
          hash: balance.token.address,
          name: balance.token.symbol,
          symbol: balance.token.symbol,
        }

        balances.push({
          amount: String(Number(balance.value) / 10 ** token.decimals),
          token,
        })
      } catch {
        /* empty */
      }
    })

    return balances
  }
}
